/**
 * Money formatting helpers — promoted from
 * `apps/mobile/src/lib/cost.ts` per ADR-0076 / Phase 4 so the new
 * `MoneyText` primitive (and any other lib consumer) can read them
 * without depending on apps-level modules.
 *
 * The shape matches `Money` from `libs/itinerary-types` by duck-
 * typing — `{ amount, currency }`. We don't import the itinerary type
 * to stay clear of the cross-lib import constraint documented in
 * `CLAUDE.md`.
 */

export interface MoneyLike {
  amount: number;
  currency: string;
}

/**
 * Format one Money value. Uses Intl.NumberFormat for locale-aware
 * symbols + grouping; falls back to plain "1234 XYZ" when the
 * currency code isn't recognised by the runtime (rare — tickets like
 * XAF, BTN, etc.).
 */
export function formatMoney(money: MoneyLike, locale = 'en-US'): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: money.currency,
      // Trim trailing .00 for clean amounts but keep cents when meaningful.
      maximumFractionDigits: Number.isInteger(money.amount) ? 0 : 2,
    }).format(money.amount);
  } catch {
    return `${money.amount.toLocaleString(locale)} ${money.currency}`;
  }
}

/**
 * Render a list of per-currency totals as "$1,200 + €450 + ¥30,000".
 * Empty list returns ''.
 */
export function formatTotalsByCurrency(totals: MoneyLike[], locale = 'en-US'): string {
  if (!totals.length) return '';
  return totals.map((t) => formatMoney(t, locale)).join(' + ');
}
